import React, { useMemo, useState } from 'react';
import {
  Alert, Button, Form, ListGroup, ProgressBar,
} from 'react-bootstrap';
import FileUploadZone from '../FileUploadZone';
import CounterpartyPicker, { KIND_LABELS } from '../CounterpartyPicker';
import KnowledgeBaseService, {
  AUDIENCE_LABELS, SCOPES, SCOPE_LABELS, scopeNeedsCounterparty,
} from '../../Services/KnowledgeBaseService';

/**
 * The one uploader. Two entry points use it:
 *
 *   • the Knowledge Base admin screen, where the operator chooses the scope
 *     and, for operator/partner scope, picks the counterparty from the list;
 *   • the Documents modal on an operator's or a partner's row, where the
 *     scope and the counterparty are already known and so are not asked for.
 *
 * Upload is two steps and the file never passes through our API: ask for a
 * presigned PUT, then PUT the bytes at S3 directly (see
 * `KnowledgeBaseService.uploadToPresignedUrl` for why that one call does not
 * go through Services/API).
 *
 * Files go up ONE AT A TIME on purpose. A ten-file drop issuing ten presign
 * requests and ten parallel PUTs makes a partial failure unreadable — this
 * way each row either landed or says why it did not.
 */

const statusIcon = {
  pending: 'fa-clock',
  uploading: 'fa-spinner fa-spin',
  done: 'fa-check text-success',
  error: 'fa-triangle-exclamation text-danger',
};

function KnowledgeBaseUploader({
  knowledgeBases = [],
  kbId = '',
  /** Omit to fix the knowledge base (rendered as text instead of a select). */
  onKbChange = null,
  /** Set to lock the scope — the picker is then not rendered at all. */
  fixedScope = null,
  /** `{ id, name }` — set to lock the counterparty. */
  fixedCounterparty = null,
  onUploaded = null,
}) {
  const [scope, setScope] = useState(fixedScope || SCOPES.GENERAL);
  const [counterpartyId, setCounterpartyId] = useState(fixedCounterparty?.id || '');
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const effectiveScope = fixedScope || scope;
  const effectiveCounterpartyId = fixedCounterparty ? fixedCounterparty.id : counterpartyId;
  const needsCounterparty = scopeNeedsCounterparty(effectiveScope);
  const missingCounterparty = needsCounterparty && !effectiveCounterpartyId;

  const selectedKb = useMemo(
    () => knowledgeBases.find((kb) => kb.id === kbId) || null,
    [knowledgeBases, kbId],
  );

  const canUpload = Boolean(kbId) && files.length > 0 && !missingCounterparty && !uploading;

  const handleFilesSelected = (picked) => {
    setError('');
    setResults([]);
    setFiles((prev) => {
      // Same name AND same size AND same mtime is the same file dropped twice.
      const seen = new Set(prev.map((f) => `${f.name}|${f.size}|${f.lastModified}`));
      return [...prev, ...picked.filter((f) => !seen.has(`${f.name}|${f.size}|${f.lastModified}`))];
    });
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!canUpload) return;
    setUploading(true);
    setError('');
    setProgress(0);

    const outcomes = files.map((f) => ({ name: f.name, status: 'pending', message: '' }));
    setResults([...outcomes]);

    let anyLanded = false;

    // Sequential by design — see the module comment.
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      outcomes[i] = { ...outcomes[i], status: 'uploading' };
      setResults([...outcomes]);

      try {
        // eslint-disable-next-line no-await-in-loop
        const presigned = await KnowledgeBaseService.requestUploadUrl({
          kbId,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          scope: effectiveScope,
          counterpartyId: effectiveCounterpartyId,
        });
        if (!presigned?.url) throw new Error('The API returned no upload URL.');

        // eslint-disable-next-line no-await-in-loop
        await KnowledgeBaseService.uploadToPresignedUrl(
          presigned.url,
          file,
          (pct) => setProgress(pct),
        );
        outcomes[i] = { ...outcomes[i], status: 'done', message: presigned.key || '' };
        anyLanded = true;
      } catch (err) {
        outcomes[i] = {
          ...outcomes[i],
          status: 'error',
          message: err.response?.data?.message || err.message || 'Upload failed',
        };
      }
      setResults([...outcomes]);
    }

    setUploading(false);
    setProgress(0);
    // Keep only what failed, so "Upload" after a partial failure retries
    // exactly the files that did not land rather than re-sending the lot.
    const failedNames = new Set(
      outcomes.filter((o) => o.status === 'error').map((o) => o.name),
    );
    setFiles((prev) => prev.filter((f) => failedNames.has(f.name)));
    if (anyLanded && onUploaded) onUploaded();
  };

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      {/* Which knowledge base. Static text when the caller fixed it. */}
      {onKbChange ? (
        <Form.Group className="mb-3" controlId="kb-uploader-kb">
          <Form.Label>Knowledge base</Form.Label>
          <Form.Select value={kbId} onChange={(e) => onKbChange(e.target.value)}>
            <option value="">Select a knowledge base…</option>
            {knowledgeBases.map((kb) => (
              <option key={kb.id} value={kb.id}>
                {`${kb.name} (${AUDIENCE_LABELS[kb.audience] || kb.audience})`}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      ) : (
        selectedKb && (
          <div className="mb-3 small text-muted">
            Uploading into
            {' '}
            <strong>{selectedKb.name}</strong>
            {' '}
            <span className="badge bg-light text-dark border">
              {AUDIENCE_LABELS[selectedKb.audience] || selectedKb.audience}
            </span>
          </div>
        )
      )}

      {/* Scope. Hidden entirely when the caller fixed it — an upload started
          from an operator's own row has nothing to choose. */}
      {fixedScope ? (
        <Alert variant="light" className="border py-2 px-3 mb-3 small">
          <i className="fa fa-lock me-2 text-muted" aria-hidden="true" />
          Scope:
          {' '}
          <strong>{SCOPE_LABELS[fixedScope]}</strong>
          {fixedCounterparty && (
            <>
              {' · '}
              <strong>{fixedCounterparty.name || fixedCounterparty.id}</strong>
            </>
          )}
        </Alert>
      ) : (
        <>
          <Form.Group className="mb-3">
            <Form.Label>Who does this document apply to?</Form.Label>
            <div className="d-flex gap-3 flex-wrap">
              {Object.values(SCOPES).map((value) => (
                <Form.Check
                  key={value}
                  type="radio"
                  name="kb-upload-scope"
                  id={`kb-upload-scope-${value}`}
                  label={SCOPE_LABELS[value]}
                  value={value}
                  checked={scope === value}
                  onChange={() => {
                    setScope(value);
                    setCounterpartyId('');
                  }}
                />
              ))}
            </div>
            <Form.Text className="text-muted">
              General applies to everybody. Operator or Partner attaches the
              document to one counterparty — a question asked about them gets
              their documents AND the general ones.
            </Form.Text>
          </Form.Group>

          {needsCounterparty && (
            <div className="mb-3">
              <CounterpartyPicker
                kind={scope}
                value={counterpartyId}
                onChange={(id) => setCounterpartyId(id)}
                id={`kb-upload-${scope}`}
                label={`Which ${KIND_LABELS[scope].toLowerCase()}?`}
                isInvalid={missingCounterparty}
                feedback={`Pick the ${KIND_LABELS[scope].toLowerCase()} this document belongs to.`}
              />
            </div>
          )}
        </>
      )}

      <FileUploadZone
        onFilesSelected={handleFilesSelected}
        selectedFiles={files}
        onRemoveFile={handleRemoveFile}
      />

      {uploading && progress > 0 && (
        <ProgressBar now={progress} label={`${progress}%`} className="mt-3" />
      )}

      {results.length > 0 && (
        <ListGroup className="mt-3">
          {results.map((r) => (
            <ListGroup.Item key={r.name} className="d-flex align-items-center gap-2 py-2">
              <i className={`fa ${statusIcon[r.status]}`} aria-hidden="true" />
              <span className="flex-grow-1 text-truncate">{r.name}</span>
              {r.status === 'error' && <small className="text-danger">{r.message}</small>}
              {r.status === 'done' && <small className="text-success">Uploaded</small>}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      <div className="d-flex align-items-center gap-2 mt-3">
        <Button variant="primary" disabled={!canUpload} onClick={handleUpload}>
          {uploading ? (
            <>
              <i className="fa fa-spinner fa-spin me-2" aria-hidden="true" />
              Uploading…
            </>
          ) : (
            <>
              <i className="fa fa-cloud-arrow-up me-2" aria-hidden="true" />
              {files.length > 1 ? `Upload ${files.length} documents` : 'Upload document'}
            </>
          )}
        </Button>
        {!kbId && <small className="text-muted">Select a knowledge base first.</small>}
        {kbId && missingCounterparty && (
          <small className="text-muted">
            {`Pick the ${KIND_LABELS[effectiveScope]?.toLowerCase() || 'counterparty'} first.`}
          </small>
        )}
      </div>
    </div>
  );
}

export default KnowledgeBaseUploader;
